/**
 * TanStack Query Hook Factory - createQueryHook
 *
 * Generic factory for creating standardized query hooks with:
 * - Automatic API client injection via useApiClient()
 * - Built-in logging support
 * - Type-safe parameters and return types
 * - Configurable stale time and options
 * - Consistent error handling
 *
 * @example
 * ```typescript
 * // Create a hook for fetching a document
 * export const useDocument = createQueryHook({
 *   queryKey: (id: string) => ['documents', 'detail', id],
 *   queryFn: (apiClient, id) =>
 *     apiClient.getDocumentApiDocumentsDocumentIdGet(id).then(r => r.data),
 *   logContext: 'GET /api/documents/:id',
 *   staleTime: QUERY_STALE_TIME.LONG,
 * });
 *
 * // Usage in component:
 * const { data, isLoading, error } = useDocument('doc-123');
 * ```
 */

import { useQuery, UseQueryOptions, UseQueryResult } from '@tanstack/react-query';
import { useApiClient } from '../useApiClient';
import type { ApiClient } from '../../../api/ApiClient';
import { log } from '../../../utils/errors/logger';
import { QUERY_STALE_TIME } from '../../../constants';

/**
 * Configuration for creating a query hook
 */
export interface CreateQueryHookConfig<TData, TParams = void> {
  /**
   * Query key factory function
   * Should return a unique array of values that identify this query
   *
   * @example
   * ```typescript
   * queryKey: (id: string) => ['documents', id]
   * queryKey: (params: { search?: string }) => ['users', 'list', params]
   * ```
   */
  queryKey: (params: TParams) => readonly unknown[];

  /**
   * Query function that receives API client and parameters
   * Should return a Promise with the query result
   *
   * @example
   * ```typescript
   * queryFn: (apiClient, id: string) =>
   *   apiClient.getDocumentApiDocumentsDocumentIdGet(id).then(r => r.data)
   * ```
   */
  queryFn: (apiClient: ApiClient<unknown>, params: TParams) => Promise<TData>;

  /**
   * Stale time for the query
   * Defaults to QUERY_STALE_TIME.MEDIUM (1 minute)
   *
   * Use:
   * - SHORT (30s) for real-time data
   * - MEDIUM (1m) for moderately dynamic data
   * - LONG (5m) for relatively static data
   * - VERY_LONG (30m) for rarely changing configuration
   */
  staleTime?: number;

  /**
   * Optional logging context for request/response/error logging
   * If provided, will automatically log all API interactions
   *
   * @example 'GET /api/documents/:id'
   */
  logContext?: string;

  /**
   * Custom error handler
   * Called in addition to standard error logging
   */
  onError?: (error: Error, params: TParams) => void;

  /**
   * Custom success handler
   * Called after successful query execution
   */
  onSuccess?: (data: TData, params: TParams) => void;
}

/**
 * Options that can be passed when using the created hook
 */
export interface QueryHookOptions<TData = unknown> {
  /**
   * Whether the query should run
   * Defaults to true
   */
  enabled?: boolean;

  /**
   * Additional TanStack Query options to override defaults
   */
  queryOptions?: Partial<UseQueryOptions<TData, Error>>;
}

/**
 * Factory function to create standardized TanStack Query hooks
 *
 * Creates a React hook that:
 * 1. Automatically uses the API client via useApiClient()
 * 2. Logs requests, responses, and errors (if logContext provided)
 * 3. Provides type-safe parameters and return types
 * 4. Supports custom options on usage
 *
 * @template TData - The type of data returned by the query
 * @template TParams - The type of parameters the query accepts (void if no params)
 *
 * @param config - Configuration object for the query hook
 * @returns A React hook that can be used in components
 *
 * @example
 * ```typescript
 * // Hook without parameters
 * export const useCurrentUser = createQueryHook({
 *   queryKey: () => ['auth', 'currentUser'],
 *   queryFn: (apiClient) => apiClient.getCurrentUserApiAuthMeGet().then(r => r.data),
 *   logContext: 'GET /api/auth/me',
 * });
 *
 * // Usage: const { data: user } = useCurrentUser();
 * ```
 *
 * @example
 * ```typescript
 * // Hook with parameters
 * export const useDocuments = createQueryHook({
 *   queryKey: (params: { search?: string; limit?: number }) => ['documents', 'list', params],
 *   queryFn: (apiClient, params) =>
 *     apiClient.getDocumentsApiDocumentsGet({ query: params }).then(r => r.data),
 *   logContext: 'GET /api/documents',
 *   staleTime: QUERY_STALE_TIME.SHORT,
 * });
 *
 * // Usage: const { data: docs } = useDocuments({ search: 'test', limit: 10 });
 * ```
 */
export function createQueryHook<TData, TParams = void>(
  config: CreateQueryHookConfig<TData, TParams>
): (
  params: TParams,
  options?: QueryHookOptions<TData>
) => UseQueryResult<TData, Error> {
  return (params: TParams, options?: QueryHookOptions<TData>) => {
    const apiClient = useApiClient();

    return useQuery<TData, Error>({
      queryKey: config.queryKey(params),
      queryFn: async () => {
        // Log request if context provided
        if (config.logContext) {
          log.api.request('GET', config.logContext, params);
        }

        try {
          const result = await config.queryFn(apiClient, params);

          // Log response if context provided
          if (config.logContext) {
            log.api.response('GET', config.logContext, 200, result);
          }

          // Call custom success handler
          if (config.onSuccess) {
            config.onSuccess(result, params);
          }

          return result;
        } catch (error) {
          // Log error
          if (config.logContext) {
            log.api.error('GET', config.logContext, error as Error);
          }

          // Call custom error handler
          if (config.onError) {
            config.onError(error as Error, params);
          }

          throw error;
        }
      },
      enabled: options?.enabled !== false,
      staleTime: config.staleTime ?? QUERY_STALE_TIME.MEDIUM,
      ...options?.queryOptions,
    });
  };
}

/**
 * Overload for hooks without parameters
 * Allows calling the hook without passing any arguments
 *
 * @example
 * ```typescript
 * export const useSystemConfig = createQueryHookNoParams({
 *   queryKey: () => ['system', 'config'],
 *   queryFn: (apiClient) => apiClient.getSystemConfigApiSystemGet().then(r => r.data),
 * });
 *
 * // Usage: const { data: config } = useSystemConfig();
 * ```
 */
export function createQueryHookNoParams<TData>(
  config: Omit<CreateQueryHookConfig<TData, void>, 'queryKey'> & {
    queryKey: () => readonly unknown[];
  }
): (options?: QueryHookOptions<TData>) => UseQueryResult<TData, Error> {
  const hook = createQueryHook<TData, void>(config as CreateQueryHookConfig<TData, void>);

  return (options?: QueryHookOptions<TData>) => {
    return hook(undefined as void, options);
  };
}
